/**
 * Withdrawal safety rules (spec §7) and the price helpers they need.
 * The defaults below are the spec's table; the owner can override them in the settings table
 * (admin commands now, admin panel in Phase 7). USD amounts are integer CENTS.
 */
import { getAsset } from "./chains.js";
import { parseUnits } from "./money.js";
export const DEFAULT_WITHDRAWAL_POLICY = {
    testnet: {
        maxPerWithdrawalUsdCents: null,
        maxPer24hUsdCents: null,
        approvalAboveUsdCents: 1000_00,
        whitelistCooldownHours: 0,
        require2fa: true,
        requireEmailConfirmation: false,
        hotWalletCapUsdCents: null,
    },
    mainnet: {
        maxPerWithdrawalUsdCents: 50_00,
        maxPer24hUsdCents: 100_00,
        approvalAboveUsdCents: 25_00,
        whitelistCooldownHours: 24,
        require2fa: true,
        requireEmailConfirmation: true,
        hotWalletCapUsdCents: 200_00,
    },
};
/**
 * The SIGNER's own last line of defence (spec §6.2): a rolling 24-hour cap per chain and asset,
 * in human units, enforced inside the signer regardless of what the API/workers ask for.
 * Mainnet values are ≈ $250 so they sit just above the spec's user limits.
 */
const SIGNER_DAILY_LIMITS = {
    testnet: { ETH: "5", BNB: "5", TRX: "20000", BTC: "0.05", SOL: "20", USDT: "5000", USDC: "5000" },
    mainnet: { ETH: "0.08", BNB: "0.4", TRX: "1000", BTC: "0.003", SOL: "1.5", USDT: "250", USDC: "250" },
};
/** Signer daily cap in base units for (chain, asset). 0 = the signer refuses this asset. */
export function signerDailyLimit(mode, chain, symbol) {
    const human = SIGNER_DAILY_LIMITS[mode][symbol];
    if (!human)
        return 0n;
    return parseUnits(human, getAsset(mode, chain, symbol).decimals);
}
/**
 * The most network fee the signer will ever agree to pay for one withdrawal, per chain,
 * in the chain's native coin (human units). Bitcoin is in BTC; Tron is the TRC-20 fee limit.
 * Testnet caps are looser because testnet gas prices spike wildly.
 */
const SIGNER_MAX_NETWORK_FEE = {
    testnet: {
        ethereum: "0.05",
        base: "0.01",
        arbitrum: "0.01",
        bsc: "0.01",
        tron: "60",
        bitcoin: "0.001",
        solana: "0.01",
    },
    mainnet: {
        ethereum: "0.004",
        base: "0.0005",
        arbitrum: "0.0005",
        bsc: "0.002",
        tron: "40",
        bitcoin: "0.0002",
        solana: "0.005",
    },
};
/** Signer's network-fee cap for a chain, in the native coin's base units. */
export function signerMaxNetworkFee(mode, chain, nativeDecimals) {
    return parseUnits(SIGNER_MAX_NETWORK_FEE[mode][chain], nativeDecimals);
}
/**
 * Highest gas limit for one EVM transfer (workers never ask for more; the signer refuses more).
 * Gas is always ESTIMATED — never assumed — because network upgrades change it: on Sepolia
 * (Oct 2026) sending ETH to a brand-new address costs ~207,000 gas (not 21,000) and a USDC
 * transfer ~154,000. The real money cap is the signer's max network fee above.
 */
export const EVM_GAS_CEILING = { native: 300000n, token: 300000n };
/** CoinGecko ids for USD prices. Testnet coins are valued like their mainnet versions. */
export const COINGECKO_IDS = {
    ETH: "ethereum",
    BNB: "binancecoin",
    TRX: "tron",
    BTC: "bitcoin",
    SOL: "solana",
    USDT: "tether",
    USDC: "usd-coin",
};
/** Price as integer micro-USD (1 USD = 1,000,000). */
export const MICRO_USD = 1000000n;
/** USD value in cents (rounded UP, so limits are never under-counted). */
export function usdCents(amount, decimals, priceMicroUsd) {
    const numerator = amount * priceMicroUsd; // base units × µUSD
    const denominator = 10n ** BigInt(decimals) * 10000n; // → cents
    return (numerator + denominator - 1n) / denominator;
}
/** Converts an API price (number like 3012.45) to micro-USD without float drift. */
export function priceToMicroUsd(price) {
    if (!Number.isFinite(price) || price < 0)
        throw new Error(`Invalid price: ${price}`);
    return parseUnits(price.toFixed(6), 6);
}
//# sourceMappingURL=withdrawal-policy.js.map